import { MapPin, Check, Star, ArrowRight, Phone, Shield, Heart, Cable, Users, Wifi, Tv, Building2, Smartphone, Radio, Server, Network, Zap, Lock, ChevronRight, Globe, Headphones, Camera, Clock, Award, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import CanariasDayPopup from "../components/CanariasDayPopup";
import { useT, useLang } from "../hooks/useT";

const HomePage = ({ onCheckCoverage, onContract }) => {
  const t = useT();
  const lang = useLang();
  return (
    <>
      <CanariasDayPopup />
      <SEO
        title="Operador canario de fibra, móvil, TV y eSIM"
        description="Fibra óptica FTTH hasta 1Gb, móvil sin permanencia desde 6,99€, TV y eSIM Travel en 180 países. Operador 100% canario con red propia en las 8 islas."
        path="/"
        keywords="operador canario, fibra Canarias, fibra Gran Canaria, fibra Tenerife, fibra Lanzarote, móvil Canarias, eSIM Canarias, internet Canarias, LiveWifi, fibra sin permanencia"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://livewifi.es/" }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "¿Qué cobertura de fibra tiene LiveWifi en Canarias?",
                "acceptedAnswer": { "@type": "Answer", "text": "LiveWifi ofrece fibra FTTH y red WiMAX rural en las 8 Islas Canarias: Gran Canaria, Tenerife, Lanzarote, Fuerteventura, La Palma, La Gomera, El Hierro y La Graciosa." }
              },
              {
                "@type": "Question",
                "name": "¿Las tarifas móviles tienen permanencia?",
                "acceptedAnswer": { "@type": "Answer", "text": "No. Todas las tarifas móviles de LiveWifi son sin permanencia. Pagas lo que contratas, sin sorpresas ni subidas." }
              },
              {
                "@type": "Question",
                "name": "¿Qué es LiveWifi Travel?",
                "acceptedAnswer": { "@type": "Answer", "text": "Es la eSIM internacional de LiveWifi. Funciona en 180 países con activación instantánea por QR. Comprada en Canarias, usada en cualquier parte del mundo." }
              },
              {
                "@type": "Question",
                "name": "¿Desde cuándo opera LiveWifi en Canarias?",
                "acceptedAnswer": { "@type": "Answer", "text": "LiveWifi es un operador canario registrado en España con infraestructura propia (datacenter en Canarias, red FTTH y WiMAX), atención local desde Teror, Gran Canaria." }
              }
            ]
          }
        ]}
      />

      {/* ═══════════════════════════════════════════
          HERO — EL GOLPE DEFINITIVO
      ═══════════════════════════════════════════ */}
      <section id="hero" data-testid="hero-section" className="relative pt-32 lg:pt-40 pb-20 lg:pb-32 overflow-hidden min-h-[95vh] flex items-center">
        <div className="absolute inset-0 bg-[#0A0A0A]" />
        <div className="absolute inset-0 opacity-[0.025]" style={{backgroundImage: "radial-gradient(circle, #7DD500 1px, transparent 1px)", backgroundSize: "40px 40px"}} />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#7DD500]/[0.04] rounded-full blur-[200px] pointer-events-none translate-x-1/4 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#7DD500]/[0.03] rounded-full blur-[150px] pointer-events-none -translate-x-1/3 translate-y-1/3" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Izquierda — Mensaje */}
            <div>
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#7DD500]/30 bg-[#7DD500]/10 mb-8">
                <div className="w-2 h-2 rounded-full bg-[#7DD500] animate-pulse" />
                <span className="text-[#7DD500] text-xs font-bold tracking-[0.2em] uppercase">{t("home_eyebrow")}</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 tracking-tight leading-[1.02]">
                {t("home_h1_a")}<br />{t("home_h1_b")}
              </h1>
              <p className="text-3xl md:text-4xl lg:text-[2.75rem] font-black text-[#7DD500] mb-8 tracking-tight leading-[1.1]">
                {t("home_h1_accent_a")}<br />{t("home_h1_accent_b")}
              </p>
              
              <p className="text-lg md:text-xl text-[#A1A1AA] mb-10 leading-relaxed max-w-lg font-light">
                {t("home_h1_sub")}
              </p>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/fibra-movil" data-testid="hero-btn-tarifas" className="bg-[#7DD500] text-[#0A0A0A] font-bold tracking-wide rounded-lg px-8 py-4 transition-all hover:bg-[#8DF000] hover:shadow-[0_0_30px_rgba(125,213,0,0.4)] flex items-center gap-2 text-lg">
                  {t("home_btn_tarifas")} <ArrowRight className="w-5 h-5" strokeWidth={2} />
                </Link>
                <Link to="/empresas" data-testid="hero-btn-empresas" className="bg-transparent text-white border-2 border-white/20 rounded-lg px-8 py-4 transition-all hover:border-[#7DD500] hover:text-[#7DD500] font-bold tracking-wide text-lg">
                  {t("home_btn_empresas")}
                </Link>
                <button onClick={onCheckCoverage} data-testid="hero-coverage-btn" className="bg-transparent text-[#7DD500] border-2 border-[#7DD500]/40 rounded-lg px-8 py-4 transition-all hover:bg-[#7DD500]/10 font-bold tracking-wide flex items-center gap-2 text-lg">
                  <MapPin className="w-5 h-5" strokeWidth={2} /> {t("home_btn_cobertura")}
                </button>
              </div>
            </div>

            {/* Derecha — Números que impactan */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#141414]/80 border border-white/[0.08] rounded-2xl p-7 backdrop-blur-sm">
                  <Cable className="w-7 h-7 text-[#7DD500] mb-4" strokeWidth={1.5} />
                  <div className="text-4xl font-black text-white tracking-tight mb-1">FTTH</div>
                  <div className="text-sm text-[#A1A1AA]">{t("home_stat_ftth_label")}</div>
                  <div className="text-xs text-[#7DD500]/70 mt-2 font-semibold">{t("home_stat_ftth_sub")}</div>
                </div>
                <div className="bg-[#141414]/80 border border-white/[0.08] rounded-2xl p-7 backdrop-blur-sm">
                  <Server className="w-7 h-7 text-[#7DD500] mb-4" strokeWidth={1.5} />
                  <div className="text-4xl font-black text-white tracking-tight mb-1">DC</div>
                  <div className="text-sm text-[#A1A1AA]">{t("home_stat_dc_label")}</div>
                  <div className="text-xs text-[#7DD500]/70 mt-2 font-semibold">{t("home_stat_dc_sub")}</div>
                </div>
                <div className="bg-[#141414]/80 border border-white/[0.08] rounded-2xl p-7 backdrop-blur-sm">
                  <Globe className="w-7 h-7 text-[#7DD500] mb-4" strokeWidth={1.5} />
                  <div className="text-4xl font-black text-white tracking-tight mb-1">70%</div>
                  <div className="text-sm text-[#A1A1AA]">{t("home_stat_70_label")}</div>
                  <div className="text-xs text-[#7DD500]/70 mt-2 font-semibold">{t("home_stat_70_sub")}</div>
                </div>
                <div className="bg-[#141414]/80 border border-[#7DD500]/20 rounded-2xl p-7 backdrop-blur-sm">
                  <Shield className="w-7 h-7 text-[#7DD500] mb-4" strokeWidth={1.5} />
                  <div className="text-4xl font-black text-[#7DD500] tracking-tight mb-1">0</div>
                  <div className="text-sm text-[#A1A1AA]">{t("home_stat_0_label")}</div>
                  <div className="text-xs text-[#7DD500]/70 mt-2 font-semibold">{t("home_stat_0_sub")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust strip — móvil */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#A1A1AA] mt-10 lg:hidden">
            <div className="flex items-center gap-2"><Cable className="w-4 h-4 text-[#7DD500]" strokeWidth={2} /><span>{t("home_trust_ftth")}</span></div>
            <div className="flex items-center gap-2"><Server className="w-4 h-4 text-[#7DD500]" strokeWidth={2} /><span>{t("home_trust_dc")}</span></div>
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#7DD500]" strokeWidth={2} /><span>{t("home_trust_no_print")}</span></div>
            <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#7DD500]" strokeWidth={2} /><span>{t("home_trust_islands")}</span></div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          DE CANARIAS, PARA CANARIAS
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-[#050505]" data-testid="canarias-section">
        <div className="h-px bg-gradient-to-r from-transparent via-[#7DD500] to-transparent" />
        <div className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              {/* Columna izquierda — Mensaje */}
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7DD500]/10 border border-[#7DD500]/20 mb-6">
                  <MapPin className="w-4 h-4 text-[#7DD500]" strokeWidth={2} />
                  <span className="text-[#7DD500] text-xs font-bold tracking-[0.15em]">{t("home_canarias_eyebrow")}</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] mb-6">
                  {t("home_canarias_h2_a")}<br /><span className="text-[#7DD500]">{t("home_canarias_h2_b")}</span>
                </h2>
                <p className="text-lg md:text-xl text-[#A1A1AA] leading-relaxed mb-6 max-w-2xl">
                  {t("home_canarias_p1")}
                </p>
                <p className="text-base text-[#A1A1AA] leading-relaxed mb-8 max-w-2xl">
                  {t("home_canarias_p2")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    t("home_canarias_tag1"),
                    t("home_canarias_tag2"),
                    t("home_canarias_tag3"),
                    t("home_canarias_tag4"),
                  ].map((tag, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10">
                      <Check className="w-3.5 h-3.5 text-[#7DD500]" strokeWidth={2} />
                      <span className="text-white/80 text-sm font-medium">{tag}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Columna derecha — Islas */}
              <div className="lg:col-span-2">
                <div className="relative p-8 rounded-3xl bg-[#141414] border border-white/10 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#7DD500]/[0.04] to-transparent" />
                  <div className="relative">
                    <p className="text-xs text-[#7DD500] font-bold tracking-[0.2em] mb-6 text-center">{t("home_islands_title")}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { isla: "Gran Canaria", tipo: "Fibra + WiMAX" },
                        { isla: "Tenerife", tipo: "Fibra + WiMAX" },
                        { isla: "Lanzarote", tipo: "Fibra + WiMAX" },
                        { isla: "Fuerteventura", tipo: "Fibra + WiMAX" },
                        { isla: "La Palma", tipo: "WiMAX" },
                        { isla: "La Gomera", tipo: "WiMAX" },
                        { isla: "El Hierro", tipo: "WiMAX" },
                        { isla: "La Graciosa", tipo: t("home_island_studied") },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.tipo === t("home_island_studied") ? "bg-orange-400" : "bg-[#7DD500]"}`} />
                          <div className="min-w-0">
                            <p className="text-white text-sm font-semibold truncate">{item.isla}</p>
                            <p className="text-white/40 text-[10px]">{item.tipo}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/10 text-center">
                      <p className="text-3xl font-black text-[#7DD500] tracking-tight">468.000+</p>
                      <p className="text-white/50 text-xs mt-1">{t("home_islands_total")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          WIMAX HÉROE — Llegamos donde la fibra no llega
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden" data-testid="wimax-hero-section">
        <div className="h-px bg-gradient-to-r from-transparent via-[#7DD500] to-transparent" />
        <div className="py-20 lg:py-28 bg-gradient-to-br from-[#0A0A0A] via-[#0d1f00]/30 to-[#0A0A0A] relative">
          {/* Decorative grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: "linear-gradient(#7DD500 1px, transparent 1px), linear-gradient(90deg, #7DD500 1px, transparent 1px)", backgroundSize: "60px 60px"}} />
          <div className="absolute top-1/4 -right-32 w-96 h-96 bg-[#7DD500]/[0.06] rounded-full blur-[120px]" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left column — Message */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7DD500]/10 border border-[#7DD500]/30 mb-6">
                  <svg className="w-4 h-4 text-[#7DD500]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z"/></svg>
                  <span className="text-[#7DD500] text-xs font-bold tracking-[0.2em]">{t("home_wimax_eyebrow")}</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] mb-3">
                  {t("home_wimax_h2_a")}<br />{t("home_wimax_h2_b")}
                </h2>
                <p className="text-3xl md:text-4xl font-black text-[#7DD500] tracking-tight leading-[1.05] mb-8">
                  {t("home_wimax_h2_accent")}
                </p>
                <p className="text-lg text-[#A1A1AA] leading-relaxed mb-8 max-w-2xl">
                  {t("home_wimax_p1")}
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {[
                    t("home_wimax_feat1"),
                    t("home_wimax_feat2"),
                    t("home_wimax_feat3"),
                    t("home_wimax_feat4"),
                  ].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                      <div className="w-7 h-7 rounded-full bg-[#7DD500]/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-[#7DD500]" strokeWidth={3} />
                      </div>
                      <span className="text-white/85 text-sm font-medium">{feat}</span>
                    </div>
                  ))}
                </div>
                <a
                  href="https://wa.me/34601651014?text=Hola%2C%20me%20interesa%20saber%20si%20WiMAX%20llega%20a%20mi%20zona.%20Mi%20direcci%C3%B3n%20es%3A%20"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wimax-cta-whatsapp"
                  className="inline-flex items-center gap-2 bg-[#7DD500] text-[#0A0A0A] font-bold rounded-lg px-8 py-4 text-lg hover:bg-[#8DF000] hover:shadow-[0_0_30px_rgba(125,213,0,0.4)] transition-all"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                  Pregúntanos por WhatsApp
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </a>
              </div>

              {/* Right column — Coverage card */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#7DD500]/30 to-transparent rounded-3xl blur-2xl opacity-50" />
                <div className="relative p-8 lg:p-10 rounded-3xl bg-[#141414] border border-[#7DD500]/20 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#7DD500]/[0.05] via-transparent to-transparent" />
                  <div className="relative">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-[#7DD500]/15 border border-[#7DD500]/30 flex items-center justify-center flex-shrink-0">
                        <svg className="w-7 h-7 text-[#7DD500]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75a4.5 4.5 0 014.5-4.5h10.5a4.5 4.5 0 014.5 4.5v10.5a4.5 4.5 0 01-4.5 4.5H6.75a4.5 4.5 0 01-4.5-4.5V6.75z"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/></svg>
                      </div>
                      <div>
                        <p className="text-xs text-[#7DD500] font-bold tracking-[0.15em] uppercase mb-1">{t("home_wimax_cases_title")}</p>
                        <p className="text-white/70 text-sm leading-relaxed">{t("home_wimax_cases")}</p>
                      </div>
                    </div>
                    <div className="border-t border-white/10 pt-6 mt-6">
                      <div className="flex items-baseline gap-3 mb-2">
                        <p className="text-5xl font-black text-white tracking-tight">100<span className="text-2xl text-[#7DD500]">Mb</span></p>
                        <p className="text-[#A1A1AA] text-sm">{t("home_wimax_feat1")}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <div className="w-2 h-2 rounded-full bg-[#7DD500] animate-pulse" />
                        <p className="text-white/60 text-xs font-medium">{t("home_wimax_feat3")} · {t("home_wimax_feat4")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          INFRAESTRUCTURA PROPIA — EL DIFERENCIADOR
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden" data-testid="infraestructura-section">
        <div className="h-px bg-gradient-to-r from-transparent via-[#7DD500] to-transparent" />
        
        <div className="py-24 lg:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7DD500]/10 border border-[#7DD500]/20 mb-6">
                  <Award className="w-4 h-4 text-[#7DD500]" strokeWidth={2} />
                  <span className="text-[#7DD500] text-xs font-bold tracking-[0.15em]">{t("home_infra_eyebrow")}</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight mb-6 leading-tight">
                  {t("home_infra_h2_a")}<br /><span className="text-[#7DD500]">{t("home_infra_h2_b")}</span>
                </h2>
                <p className="text-[#A1A1AA] text-lg leading-relaxed mb-8">
                  {t("home_infra_p1")}
                </p>
                <p className="text-[#A1A1AA] text-base leading-relaxed">
                  {t("home_infra_p2")}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { Icon: Cable, title: t("home_infra_card_ftth_t"), desc: t("home_infra_card_ftth_d"), stat: "FTTH", accent: false },
                  { Icon: Server, title: t("home_infra_card_core_t"), desc: t("home_infra_card_core_d"), stat: "CORE", accent: false },
                  { Icon: Network, title: t("home_infra_card_dc_t"), desc: t("home_infra_card_dc_d"), stat: "DC", accent: false },
                  { Icon: Radio, title: t("home_infra_card_rural_t"), desc: t("home_infra_card_rural_d"), stat: "WIMAX", accent: true },
                ].map((item, i) => (
                  <div key={i} className={`relative bg-[#141414] border ${item.accent ? "border-[#7DD500]/30" : "border-white/10"} rounded-2xl p-6 hover:-translate-y-1 transition-transform overflow-hidden`}>
                    <div className="absolute top-3 right-3 text-[#7DD500]/[0.07] text-4xl font-black tracking-tighter select-none">{item.stat}</div>
                    <item.Icon className="w-7 h-7 text-[#7DD500] mb-4 relative z-10" strokeWidth={1.5} />
                    <h3 className="text-white font-bold tracking-tight mb-2 text-sm relative z-10">{item.title}</h3>
                    <p className="text-[#A1A1AA] text-xs leading-relaxed relative z-10">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Zonas de despliegue */}
            <div className="text-center">
              <p className="text-xs text-[#A1A1AA]/60 tracking-widest uppercase mb-4">{t("home_infra_zones_label")}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {["Teror", "Valleseco", "Firgas", "Moya", "Arucas", "San Mateo", "Santa Brígida", "Las Palmas de GC", "Telde", "Valsequillo", "Ingenio", "Agüimes", "Santa Lucía de Tirajana"].map((zona) => (
                  <span key={zona} className="px-3 py-1 text-xs font-medium rounded-full bg-[#141414] text-[#A1A1AA] border border-white/10">{zona}</span>
                ))}
              </div>
              <p className="text-[#A1A1AA]/40 text-xs mt-3 italic">{t("home_infra_zones_more")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          DOBLE ENTRADA: PARTICULARES / EMPRESAS
      ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="dual-entry">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">{t("home_dual_h2")}</h2>
            <p className="text-[#A1A1AA] text-lg">{t("home_dual_sub")}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* PARTICULARES */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 md:p-10 hover:border-[#7DD500]/20 transition-all group" data-testid="entry-particulares">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#7DD500]/10 flex items-center justify-center">
                  <Wifi className="w-5 h-5 text-[#7DD500]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">{t("home_dual_part_h")}</h3>
              </div>
              <p className="text-[#A1A1AA] mb-8 leading-relaxed">{t("home_dual_part_p")}</p>
              
              <div className="space-y-3 mb-8">
                {[
                  { to: "/fibra-movil", Icon: Cable, label: t("home_prod_p1_label"), price: t("home_prod_p1_price") },
                  { to: "/tarifas-movil", Icon: Smartphone, label: t("home_prod_p2_label"), price: t("home_prod_p2_price") },
                  { to: "/fibra-fijo", Icon: Wifi, label: t("home_prod_p3_label"), price: t("home_prod_p3_price") },
                  { to: "/tv", Icon: Tv, label: t("home_prod_p4_label"), price: t("home_prod_p4_price") },
                ].map((item) => (
                  <Link key={item.to} to={item.to} data-testid={`entry-${item.to.slice(1)}`} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#7DD500]/30 hover:bg-[#7DD500]/[0.03] transition-all group/link">
                    <div className="flex items-center gap-3">
                      <item.Icon className="w-5 h-5 text-[#7DD500]" strokeWidth={1.5} />
                      <span className="text-white font-semibold text-sm">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#A1A1AA] text-xs">{item.price}</span>
                      <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover/link:text-[#7DD500] group-hover/link:translate-x-1 transition-all" strokeWidth={1.5} />
                    </div>
                  </Link>
                ))}
              </div>
              
              <Link to="/fibra-movil" className="w-full py-3.5 bg-[#7DD500] text-[#0A0A0A] rounded-lg font-bold hover:bg-[#8DF000] transition-all flex items-center justify-center gap-2">
                {t("home_dual_part_cta")} <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>

            {/* EMPRESAS */}
            <div className="bg-[#141414] border border-[#7DD500]/20 rounded-2xl p-8 md:p-10 relative overflow-hidden" data-testid="entry-empresas">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#7DD500]" />
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#7DD500]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#7DD500]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">{t("home_dual_emp_h")}</h3>
                <span className="px-2.5 py-0.5 text-[10px] font-bold bg-[#7DD500] text-[#0A0A0A] rounded-full tracking-wider">{t("home_dual_emp_badge")}</span>
              </div>
              <p className="text-[#A1A1AA] mb-8 leading-relaxed">{t("home_dual_emp_p")}</p>
              
              <div className="space-y-3 mb-8">
                {[
                  { Icon: Headphones, label: t("home_prod_b1_label"), desc: t("home_prod_b1_desc") },
                  { Icon: Lock, label: t("home_prod_b2_label"), desc: t("home_prod_b2_desc") },
                  { Icon: Zap, label: t("home_prod_b3_label"), desc: t("home_prod_b3_desc") },
                  { Icon: Network, label: t("home_prod_b4_label"), desc: t("home_prod_b4_desc") },
                  { Icon: Camera, label: t("home_prod_b5_label"), desc: t("home_prod_b5_desc") },
                ].map((item, i) => (
                  <Link key={i} to="/empresas" className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#7DD500]/30 hover:bg-[#7DD500]/[0.03] transition-all group/link">
                    <div className="flex items-center gap-3">
                      <item.Icon className="w-5 h-5 text-[#7DD500]" strokeWidth={1.5} />
                      <span className="text-white font-semibold text-sm">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#A1A1AA] text-xs">{item.desc}</span>
                      <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover/link:text-[#7DD500] group-hover/link:translate-x-1 transition-all" strokeWidth={1.5} />
                    </div>
                  </Link>
                ))}
              </div>
              
              <Link to="/empresas" className="w-full py-3.5 border-2 border-[#7DD500] text-[#7DD500] rounded-lg font-bold hover:bg-[#7DD500] hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2">
                {t("home_dual_emp_cta")} <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          TARIFAS ESTRELLA — PRECIO QUE CONVIERTE
      ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#050505]" data-testid="featured-plans">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">{t("home_plans_h2_a")} <span className="text-[#7DD500]">{t("home_plans_h2_b")}</span></h2>
            <p className="text-[#A1A1AA] text-lg">{t("home_plans_sub")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Plan 1 */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 hover:-translate-y-1 transition-transform">
              <div className="text-xs text-[#7DD500] font-semibold tracking-widest mb-3">{t("home_plans_card1_eyebrow")}</div>
              <h3 className="text-2xl font-bold text-white mb-1 tracking-tight">{t("home_plans_card1_title")}</h3>
              <div className="mb-6">
                <span className="text-5xl font-black text-[#7DD500]">32,90</span>
                <span className="text-[#A1A1AA] ml-1">{t("home_plans_per_month")}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {t("home_plans_feat_fiber")} <strong className="text-white">600 Mb {lang === "en" ? "symmetric" : "simétricos"}</strong></li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {lang === "en" ? "Landline" : "Fijo"} <strong className="text-white">{t("home_plans_feat_fixed_inc")}</strong></li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {t("home_plans_feat_mobile")} <strong className="text-white">120GB + {t("home_plans_feat_unlimited")}</strong></li>
                <li className="flex items-center gap-3 text-[#7DD500]"><Check className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> <strong>{t("home_plans_feat_no_perma")}</strong></li>
              </ul>
              <button onClick={onContract} data-testid="plan-600-btn" className="w-full py-3.5 border border-white/20 text-white rounded-lg font-bold hover:border-[#7DD500] hover:text-[#7DD500] transition-all">{t("home_plans_btn_want")}</button>
            </div>

            {/* Plan 2 — LA MÁS VENDIDA */}
            <div className="relative bg-[#141414] border border-[#7DD500]/50 rounded-2xl p-8 shadow-[0_8px_40px_rgba(125,213,0,0.12)] overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#7DD500]" />
              <div className="absolute -top-px left-1/2 -translate-x-1/2 px-5 py-1.5 bg-[#7DD500] text-[#0A0A0A] text-xs font-bold rounded-b-lg flex items-center gap-1.5">
                <Star className="w-3 h-3" strokeWidth={2} /> {t("home_plans_card2_badge")}
              </div>
              <div className="text-xs text-[#7DD500] font-semibold tracking-widest mb-3 mt-4">{t("home_plans_card1_eyebrow")}</div>
              <h3 className="text-2xl font-bold text-white mb-1 tracking-tight">{t("home_plans_card2_title")}</h3>
              <div className="mb-6">
                <span className="text-5xl font-black text-[#7DD500]">35,90</span>
                <span className="text-[#A1A1AA] ml-1">{t("home_plans_per_month")}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {t("home_plans_feat_fiber")} <strong className="text-white">1 Gb {lang === "en" ? "symmetric" : "simétrico"}</strong></li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {lang === "en" ? "Landline" : "Fijo"} <strong className="text-white">{t("home_plans_feat_fixed_inc")}</strong></li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {t("home_plans_feat_mobile")} <strong className="text-white">120GB + {t("home_plans_feat_unlimited")}</strong></li>
                <li className="flex items-center gap-3 text-[#7DD500]"><Check className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> <strong>{t("home_plans_feat_no_perma")}</strong></li>
              </ul>
              <button onClick={onContract} data-testid="plan-1gb-btn" className="w-full py-3.5 bg-[#7DD500] text-[#0A0A0A] rounded-lg font-bold hover:bg-[#8DF000] hover:shadow-[0_0_20px_rgba(125,213,0,0.4)] transition-all">{t("home_plans_btn_want")}</button>
            </div>

            {/* Plan 3 — Solo móvil */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 hover:-translate-y-1 transition-transform">
              <div className="text-xs text-[#7DD500] font-semibold tracking-widest mb-3">{t("home_plans_card3_eyebrow")}</div>
              <h3 className="text-2xl font-bold text-white mb-1 tracking-tight">{t("home_plans_card3_title")}</h3>
              <div className="mb-6">
                <span className="text-5xl font-black text-[#7DD500]">9,64</span>
                <span className="text-[#A1A1AA] ml-1">{t("home_plans_per_month")}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> {t("home_plans_feat_calls_unlim")} <strong className="text-white">{t("home_plans_feat_unlimited")}</strong></li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> <strong className="text-white">100 GB</strong> {t("home_plans_feat_data")}</li>
                <li className="flex items-center gap-3 text-[#A1A1AA]"><Check className="w-4 h-4 text-[#7DD500] flex-shrink-0" strokeWidth={2} /> 5G + VoLTE</li>
                <li className="flex items-center gap-3 text-[#7DD500]"><Check className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> <strong>{t("home_plans_feat_no_perma_short")}</strong></li>
              </ul>
              <button onClick={onContract} data-testid="plan-movil-btn" className="w-full py-3.5 border border-white/20 text-white rounded-lg font-bold hover:border-[#7DD500] hover:text-[#7DD500] transition-all">{t("home_plans_btn_want")}</button>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/fibra-movil" className="text-[#7DD500] font-bold text-sm hover:underline underline-offset-4 inline-flex items-center gap-1">
              {t("home_plans_link_all")} <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <p className="text-[#71717A] text-xs mt-4">{t("home_plans_disclaimer")}</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          ADN LIVEWIFI — CARÁCTER DE MARCA
      ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#050505]" data-testid="why-livewifi">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">{t("home_adn_h2")}</h2>
            <p className="text-[#A1A1AA] text-lg">{t("home_adn_sub")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { Icon: Shield, title: t("home_adn_c1_t"), desc: t("home_adn_c1_d") },
              { Icon: Phone, title: t("home_adn_c2_t"), desc: t("home_adn_c2_d") },
              { Icon: Cable, title: t("home_adn_c3_t"), desc: t("home_adn_c3_d") },
              { Icon: Heart, title: t("home_adn_c4_t"), desc: t("home_adn_c4_d") },
            ].map((item, i) => (
              <div key={i} className="bg-[#141414] border border-white/10 rounded-2xl p-8 hover:-translate-y-1 transition-transform">
                <item.Icon className="w-8 h-8 text-[#7DD500] mb-5" strokeWidth={1.5} />
                <h3 className="font-black text-white mb-3 tracking-tight text-lg">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          ¿NO TE LLEGA LA FIBRA? — ESTUDIO DE ZONA
      ═══════════════════════════════════════════ */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="estudio-zona-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl border border-red-500/20 bg-gradient-to-r from-red-500/[0.06] via-transparent to-[#7DD500]/[0.06] overflow-hidden">
            <div className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
                  <MapPin className="w-3.5 h-3.5 text-red-400" strokeWidth={2} />
                  <span className="text-red-400 text-xs font-bold tracking-wider">{t("home_estudio_eyebrow")}</span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white tracking-tight mb-3 leading-tight">
                  {t("home_estudio_h2_a")}<br /><span className="text-[#7DD500]">{t("home_estudio_h2_b")}</span>
                </h2>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  {t("home_estudio_p")}
                </p>
              </div>
              <Link to="/estudio-zona" data-testid="estudio-zona-home-btn" className="bg-[#7DD500] text-[#0A0A0A] font-bold rounded-lg px-8 py-4 hover:bg-[#8DF000] hover:shadow-[0_0_30px_rgba(125,213,0,0.4)] transition-all flex items-center gap-2 text-lg whitespace-nowrap flex-shrink-0">
                {t("home_estudio_btn")} <ArrowRight className="w-5 h-5" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          TESTIMONIOS — RESEÑAS REALES DE GOOGLE
      ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="testimonios-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7DD500]/10 border border-[#7DD500]/20 mb-6">
              <Star className="w-4 h-4 text-[#7DD500] fill-[#7DD500]" strokeWidth={1.5} />
              <span className="text-[#7DD500] text-xs font-bold tracking-[0.15em]">RESEÑAS DE GOOGLE</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">Lo que dicen nuestros clientes.</h2>
            <p className="text-[#A1A1AA] text-lg">Reseñas reales de Google. <strong className="text-white">Sin filtrar.</strong></p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Juan M.", location: "Maspalomas", stars: 5, text: "600 Mbps reales, soporte de 10. Llevaba años con otro operador y no hay punto de comparación. Aquí llamas y te cogen, así de simple.", time: "Hace 2 meses" },
              { name: "Ana G.", location: "Arguineguín", stars: 5, text: "Perfecto para teletrabajo. Trabajo desde casa todos los días con videollamadas y no se ha caído ni una sola vez en 8 meses. En mi zona ningún operador grande llegaba bien.", time: "Hace 3 meses" },
              { name: "Pedro L.", location: "Telde", stars: 5, text: "En mi finca era imposible tener internet decente. Vinieron, estudiaron la zona y me montaron WiMAX. Velocidad brutal para donde estoy. Muy agradecido.", time: "Hace 4 meses" },
              { name: "Carmen R.", location: "Teror", stars: 5, text: "Por fin un operador que cumple lo que promete. Sin subidas de precio sorpresa, sin permanencias absurdas. Lo que ves en la web es lo que pagas. Ojalá hubiera cambiado antes.", time: "Hace 1 mes" },
              { name: "David S.", location: "Santa Brígida", stars: 5, text: "Tengo el pack de fibra + móvil y la verdad es que no tengo queja ninguna. El técnico que vino a instalar era de aquí, conocía la zona perfectamente. Eso se nota.", time: "Hace 5 meses" },
              { name: "María T.", location: "Arucas", stars: 4, text: "Muy contenta con el servicio. La fibra va perfecta y el precio es muy competitivo. El soporte técnico responde rápido por WhatsApp. Se nota que son de aquí.", time: "Hace 2 meses" },
            ].map((review, i) => (
              <div key={i} className="bg-[#141414] border border-white/10 rounded-2xl p-7 hover:-translate-y-1 transition-transform" data-testid={`review-${i}`}>
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className={`w-4 h-4 ${s < review.stars ? "text-yellow-400 fill-yellow-400" : "text-white/20"}`} strokeWidth={1.5} />
                  ))}
                </div>
                <p className="text-white/90 text-sm leading-relaxed mb-5 italic">&ldquo;{review.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-sm">{review.name}</p>
                    <p className="text-[#A1A1AA] text-xs">{review.location}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    <span className="text-[#A1A1AA]/50 text-xs">{review.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="https://www.google.com/search?q=LiveWifi+opiniones" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#7DD500] font-bold text-sm hover:underline underline-offset-4">
              {t("home_test_link")} <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          CTA FINAL — CIERRE
      ═══════════════════════════════════════════ */}
      <section className="py-20 lg:py-24 bg-[#0A0A0A]" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl p-10 md:p-14 bg-[#141414] border border-[#7DD500]/20 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7DD500]/50 to-transparent" />
            <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] bg-[#7DD500]/[0.04] rounded-full blur-[100px] pointer-events-none" />
            <div className="text-center relative z-10">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white tracking-tight mb-3">{t("home_cta_h2")}</h2>
              <p className="text-[#A1A1AA] mb-8 text-lg">{t("home_cta_sub")}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="tel:+34828182100" className="bg-[#7DD500] text-[#0A0A0A] font-bold tracking-wide rounded-lg px-8 py-4 transition-all hover:bg-[#8DF000] hover:shadow-[0_0_20px_rgba(125,213,0,0.4)] flex items-center gap-2 text-lg">
                  <Phone className="w-5 h-5" strokeWidth={2} /> 828 182 100
                </a>
                <a href="https://wa.me/34601651014" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white font-bold rounded-lg px-8 py-4 transition-all hover:bg-[#20BD5A] flex items-center gap-2 text-lg">
                  WhatsApp: 601 651 014
                </a>
              </div>
              <p className="text-[#A1A1AA]/50 text-xs mt-6">{t("home_cta_hours")}</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
